import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Send, Paperclip, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable manual "Send Email" dialog.
 *
 * Props:
 *  - open / onOpenChange: controls visibility
 *  - title: optional dialog title (default "Send email")
 *  - defaultCategory: pre-filter template list to one category
 *  - toUser: { id, name, email } — recipient employee (their email is the default "to")
 *  - extraPlaceholders: object of additional {{placeholders}} merged into the template
 *  - payslipId: when set, the matching payslip PDF is attached automatically
 *  - onSent: callback(log) fired after a successful send
 */
export default function SendEmailDialog({
  open,
  onOpenChange,
  title = "Send email",
  defaultCategory,
  toUser,
  extraPlaceholders,
  payslipId,
  onSent,
}) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toEmails, setToEmails] = useState("");
  const [cc, setCc] = useState("");
  const [busy, setBusy] = useState(false);
  const [senders, setSenders] = useState([]);
  const [senderEmail, setSenderEmail] = useState("");

  const loadTemplates = useCallback(async () => {
    try {
      const url = defaultCategory ? `/email/templates?category=${defaultCategory}` : "/email/templates";
      const { data } = await api.get(url);
      setTemplates(data);
      const def = data.find((t) => t.is_default) || data[0];
      if (def) setTemplateId(def.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates");
    }
  }, [defaultCategory]);

  const loadSenders = useCallback(async () => {
    try {
      const { data } = await api.get("/email/senders");
      setSenders(data || []);
    } catch (_e) {
      // non-fatal — backend will auto-resolve from settings
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadTemplates();
    loadSenders();
    if (toUser?.email) setToEmails(toUser.email);
    setCc("");
  }, [open, loadTemplates, loadSenders, toUser]);

  useEffect(() => {
    if (!templateId) { setSubject(""); setBody(""); return; }
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    setSubject(t.subject || "");
    setBody(t.html_body || "");
  }, [templateId, templates]);

  const placeholdersHint = useMemo(() => {
    if (!toUser) return "{{company_name}}, {{today}}";
    const base = ["{{employee_name}}", "{{employee_id}}", "{{role_label}}", "{{company_name}}", "{{today}}"];
    const extras = Object.keys(extraPlaceholders || {}).map((k) => `{{${k}}}`);
    return [...base, ...extras].join(" · ");
  }, [toUser, extraPlaceholders]);

  const handleSend = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!body.trim()) { toast.error("Email body is required"); return; }
    const recipients = toEmails.split(",").map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) { toast.error("At least one recipient is required"); return; }
    setBusy(true);
    try {
      const payload = {
        to_user_id: toUser?.id || null,
        to_emails: recipients,
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        sender_email: senderEmail || null,
        template_id: templateId || null,
        subject,
        html_body: body,
        category: defaultCategory || null,
        payslip_id: payslipId || null,
        placeholders: extraPlaceholders || {},
      };
      const { data } = await api.post("/email/send", payload);
      if (data.status === "sent") {
        toast.success(`Email sent to ${recipients.join(", ")}`);
        if (onSent) onSent(data);
        onOpenChange(false);
      } else {
        toast.error(data.error || "Email failed — check Email Logs");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-4 h-4" /> {title}</DialogTitle>
          <DialogDescription>
            Choose a template, edit subject/body if needed, then send. Every email is logged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {templates.length > 0 && (
            <div>
              <Label className="text-xs">Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="send-email-template"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} {t.is_default ? "(default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">To {toUser ? `· ${toUser.name}` : ""}</Label>
              <Input value={toEmails} onChange={(e) => setToEmails(e.target.value)} placeholder="employee@happyretreats.in" data-testid="send-email-to" />
            </div>
            <div>
              <Label className="text-xs">Cc (optional)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="manager@happyretreats.in" data-testid="send-email-cc" />
            </div>
          </div>
          {senders.length > 1 && (
            <div>
              <Label className="text-xs">From mailbox (override)</Label>
              <Select value={senderEmail || "auto"} onValueChange={(v) => setSenderEmail(v === "auto" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Use category default (from Email Settings)</SelectItem>
                  {senders.map((s) => <SelectItem key={s.sender_email} value={s.sender_email}>{s.sender_email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="send-email-subject" />
          </div>
          <div>
            <Label className="text-xs">Body (HTML allowed)</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              className="font-mono text-xs"
              data-testid="send-email-body"
            />
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mt-1">
              <Info className="w-3 h-3" />
              <span>Placeholders: {placeholdersHint}</span>
            </div>
          </div>
          {payslipId && (
            <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 inline-flex items-center gap-1.5">
              <Paperclip className="w-3 h-3" /> Payslip PDF will be attached automatically.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={handleSend} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="send-email-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
